import React, { useCallback, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Input,
} from "@mui/material";
import useTranslation from "../../hooks/useTranslation";

interface ChangePasswordProps {
  text: string;
  title?: string;
  truthyColor?:
    | "primary"
    | "secondary"
    | "error"
    | "info"
    | "success"
    | "warning";
  truthyText?: string;
  falsyText?: string;
  successText?: string;
  errorText?: string;
  dialogOpen: boolean;
  onConfirm: () => Promise<void>;
  setDialogOpen: (open: boolean) => void;
}

const ConfirmDialog = ({
  text,
  title,
  truthyColor,
  truthyText,
  falsyText,
  successText,
  errorText,
  dialogOpen,
  setDialogOpen,
  onConfirm,
}: ChangePasswordProps) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");

  const handleTruthy = useCallback(() => {
    setLoading(true);
    setError("");
    setSuccess("");
    onConfirm()
      .then(() => {
        setSuccess(successText || t("success"));
      })
      .catch(() => {
        setError(errorText || t("error"));
      })
      .finally(() => {
        setLoading(false);
        setDialogOpen(false);
      });
  }, [onConfirm, setDialogOpen]);

  return (
    <>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogActions className="dialogActions headerActions">
          {title && <h2 className="m0">{title}</h2>}
          <Button onClick={() => setDialogOpen(false)} color={"error"}>
            &times;
          </Button>
        </DialogActions>
        <DialogContent className="pt pb mb">
          {loading ? (
            <CircularProgress />
          ) : (
            <div dangerouslySetInnerHTML={{ __html: text }} />
          )}
          {error && (
            <div className="pt pb">
              <Alert color="error">{error}</Alert>
            </div>
          )}
          {success && (
            <div className="pt pb">
              <Alert color="success">{success}</Alert>
            </div>
          )}
        </DialogContent>
        <DialogActions className="footerActions">
          <Button
            variant="outlined"
            onClick={() => setDialogOpen(false)}
            disabled={loading}
          >
            {falsyText || t("cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleTruthy}
            disabled={loading}
            color={truthyColor || "primary"}
          >
            {truthyText || t("approve")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ConfirmDialog;
